import {
  Children,
  isValidElement,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
  type FocusEvent,
  type MouseEvent,
  type ReactNode,
} from "react";
import { Popover as AntdPopover } from "@mijadesign/web";
import { useSlotRuntime, type SlotArea } from "../../slot-runtime";

type PopoverWrapperProps = ComponentProps<typeof AntdPopover> & {
  renderSlot?: (
    slotName: string,
    payload?: Record<string, unknown>,
  ) => ReactNode;
  children?: ReactNode;
};

const slotAreas: SlotArea[] = [{ name: "trigger" }, { name: "content" }];

function PopoverWrapper({
  content,
  open,
  onOpenChange,
  renderSlot,
  children,
  trigger = "hover",
  ...restProps
}: PopoverWrapperProps) {
  const [innerOpen, setInnerOpen] = useState(false);
  const triggerRef = useRef<HTMLSpanElement | null>(null);
  const { getAreaNodes, areaMap } = useSlotRuntime({
    renderSlot,
    children,
    areas: slotAreas,
  });

  const contentNodes = useMemo(() => getAreaNodes("content"), [getAreaNodes]);
  const triggerNodes = useMemo(() => getAreaNodes("trigger"), [getAreaNodes]);
  const defaultTriggerNodes = useMemo(
    () =>
      Children.toArray(children).filter((child) => {
        if (!isValidElement(child)) return true;
        const childProps =
          child.props && typeof child.props === "object"
            ? (child.props as Record<string, unknown>)
            : {};
        return !String(childProps.__slot || "").trim();
      }),
    [children],
  );

  const resolvedContent = contentNodes?.length
    ? contentNodes.length === 1
      ? contentNodes[0]
      : contentNodes
    : content;
  const resolvedChildren = triggerNodes?.length
    ? triggerNodes.length === 1
      ? triggerNodes[0]
      : triggerNodes
    : defaultTriggerNodes.length === 1
      ? defaultTriggerNodes[0]
      : defaultTriggerNodes;

  // 拖入 content/trigger 插槽的节点仍保存在 children 下，渲染时只按命名区域消费。
  const hasNamedSlots = Object.keys(areaMap).length > 0;
  const shouldProxyTrigger = hasNamedSlots;
  const isOpenControlled = open !== undefined;
  const mergedOpen = isOpenControlled ? open : innerOpen;
  const triggerList = Array.isArray(trigger) ? trigger : [trigger];
  const updateOpen = (nextOpen: boolean) => {
    if (!isOpenControlled) {
      setInnerOpen(nextOpen);
    }
    onOpenChange?.(nextOpen);
  };

  useEffect(() => {
    if (
      !shouldProxyTrigger ||
      !mergedOpen ||
      (!triggerList.includes("click") && !triggerList.includes("contextMenu"))
    ) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (triggerRef.current?.contains(target)) return;
      if (
        target instanceof Element &&
        target.closest(".ant-popover, .ant-popover-arrow")
      ) {
        return;
      }
      updateOpen(false);
    };

    document.addEventListener("pointerdown", handlePointerDown, true);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
    };
  }, [mergedOpen, shouldProxyTrigger, triggerList]);

  const triggerNode = hasNamedSlots ? resolvedChildren : children;
  const proxyTriggerNode = shouldProxyTrigger ? (
    <span
      ref={triggerRef}
      onClickCapture={() => {
        if (triggerList.includes("click")) {
          updateOpen(!mergedOpen);
        }
      }}
      onContextMenuCapture={(event: MouseEvent<HTMLSpanElement>) => {
        if (triggerList.includes("contextMenu")) {
          event.preventDefault();
          updateOpen(!mergedOpen);
        }
      }}
      onFocusCapture={() => {
        if (triggerList.includes("focus")) {
          updateOpen(true);
        }
      }}
      onBlurCapture={(event: FocusEvent<HTMLSpanElement>) => {
        if (triggerList.includes("focus")) {
          const nextTarget = event.relatedTarget;
          if (!nextTarget || !event.currentTarget.contains(nextTarget)) {
            updateOpen(false);
          }
        }
      }}
      onMouseEnter={() => {
        if (triggerList.includes("hover")) {
          updateOpen(true);
        }
      }}
      onMouseLeave={() => {
        if (triggerList.includes("hover")) {
          updateOpen(false);
        }
      }}
    >
      {triggerNode}
    </span>
  ) : (
    triggerNode
  );

  return (
    <AntdPopover
      content={resolvedContent}
      open={mergedOpen}
      onOpenChange={updateOpen}
      trigger={shouldProxyTrigger ? [] : trigger}
      {...restProps}
    >
      {proxyTriggerNode}
    </AntdPopover>
  );
}

export default PopoverWrapper;
